import { useContext, useEffect, useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { empty, errorResponse, prepareResponseData } from "../../Utilities/utils";
import { AuthContext } from "../Root/ProtectedRoute";

// css
import "../students/Students.css";

// api
import termApi from "../../api/Terms";

//components
import MainHeader from "../../components/headers/mainHeader/MainHeader";
import AppWrapper from "../../components/appWrapper/AppWrapper";
import { Toast } from "primereact/toast";
import FullPageLoader from "../../components/loader/FullPageLoader";
import TermTable from "../../components/tables/primeTable/term/TermTable";
import TableLoading from "../../components/skeleton/TableLoading";

const Terms = ({ ...props }) => {
  const { user } = useContext(AuthContext);
  //ref
  const toastTR = useRef(null);
  // states
  const [termData, setTermData] = useState([]);
  const [termToToggleDialog, setTermToToggleDialog] = useState(false);
  const [selectedTermDetail, setSelectedTermDetail] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);

  useEffect(() => {
    // fetch terms
    try {
      getTerms();
    } catch (error) {
      responseDialog(
        "error",
        "Something went wrong",
        "Failed to load terms. Please try again later."
      );
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?._id]);

  // function to get all terms
  const getTerms = async () => {
    try {
      if (!isLoading) setIsLoading(true);
      const schoolId = !empty(user) && !empty(user._id) ? user._id : "";
      const response = await termApi.getTerms(schoolId);
      const response_data = prepareResponseData(response);
      if (empty(response_data) || !response_data.success) {
        return setTermData([]);
      } else {
        setTermData(response_data.response);
      }
    } catch (error) {
      responseDialog("error", "Error Alert!", errorResponse(error));
    } finally {
      setIsLoading(false);
    }
  };

  //alert functions
  const responseDialog = (severity = null, summary = null, detail = null) => {
    toastTR?.current?.show({
      severity,
      summary,
      detail,
      life: 8000,
    });
  };

  //function to toggle term visibility
  const toggleTermVisiblity = async () => {
    try {
      if (termToToggleDialog) setTermToToggleDialog(false);
      if (!isActionLoading) setIsActionLoading(true);
      const termId =
        !empty(selectedTermDetail) && !empty(selectedTermDetail._id)
          ? selectedTermDetail._id
          : "";
      const active =
        !empty(selectedTermDetail) &&
        !empty(selectedTermDetail.active) &&
        selectedTermDetail.active
          ? "Yes"
          : "No";
      const schoolId = !empty(user) && !empty(user._id) ? user._id : "";
      const response = await termApi.toggleTermVisibility(
        schoolId,
        termId,
        active
      );
      const response_data = prepareResponseData(response);
      if (empty(response_data) || !response_data.success) {
        const error_response =
          !empty(response_data) && !empty(response_data.response)
            ? response_data.response
            : `Failed to ${
                !empty(selectedTermDetail) &&
                !empty(selectedTermDetail.active) &&
                selectedTermDetail.active === false
                  ? "enable"
                  : "disable"
              } term with title, ${
                !empty(selectedTermDetail.title) ? selectedTermDetail.title : ""
              }`;
        return responseDialog("error", "Something went wrong", error_response);
      }

      responseDialog(
        "success",
        "Success",
        `Term, ${
          !empty(selectedTermDetail.title) ? selectedTermDetail.title : ""
        }, was ${
          !empty(selectedTermDetail) &&
          !empty(selectedTermDetail.active) &&
          selectedTermDetail.active === true
            ? "disabled"
            : "enabled"
        } successfully!`
      );
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (error) {
      responseDialog("error", "Error Alert!", errorResponse(error));
    } finally {
      setIsActionLoading(false);
    }
  };

  //function to show confirm modal
  const showDisableConfirm = (data) => {
    setSelectedTermDetail(data);
    setTermToToggleDialog(true);
  };

  //function to hide confirm modal
  const hideDisableTermDialog = () => {
    setTermToToggleDialog(false);
  };

  const disableTermDialogFooter = (
    <div
      style={{
        marginTop: 20,
      }}
    >
      <Button
        label="No"
        icon="pi pi-times"
        style={{
          backgroundColor: "transparent",
          color: "#e65061",
          borderColor: "#e65061",
          borderWidth: 1,
        }}
        onClick={hideDisableTermDialog}
      />
      <Button
        label="Yes"
        icon="pi pi-check"
        style={{
          backgroundColor: "transparent",
          color: "#389d17",
          borderColor: "#389d17",
          borderWidth: 1,
        }}
        onClick={toggleTermVisiblity}
      />
    </div>
  );

  return (
    <>
      <AppWrapper {...props}>
        <main>
          <div className="tableCard">
            {/*  header start */}
            <MainHeader title="Terms" />
            {/* end of header */}

            {/* table start */}
            {!isLoading ? (
              <TermTable
                terms={termData}
                confirmDisableSelected={showDisableConfirm}
              />
            ) : (
              <TableLoading rows={20} cols={3} />
            )}
            {/* table end  */}
          </div>
        </main>
        {isActionLoading && <FullPageLoader visible={isActionLoading} />}
      </AppWrapper>

      <Dialog
        visible={termToToggleDialog}
        style={{ width: "32rem", zIndex: 999999 }}
        breakpoints={{ "960px": "75vw", "641px": "90vw" }}
        header="Confirm"
        modal
        footer={disableTermDialogFooter}
        onHide={hideDisableTermDialog}
      >
        <div
          className="confirmation-content"
          style={{ marginTop: 20, display: "flex", alignItems: "center" }}
        >
          <i
            className="pi pi-exclamation-triangle mr-3"
            style={{ fontSize: "2rem", marginRight: 15, color: "#e65061" }}
          />
          {
            <span>
              Are you sure you want to{" "}
              {!empty(selectedTermDetail) &&
              !empty(selectedTermDetail.active) &&
              selectedTermDetail.active ? (
                <span style={{ color: "red" }}>
                  <strong>disable</strong>
                </span>
              ) : (
                <span style={{ color: "green" }}>
                  <strong>enable</strong>
                </span>
              )}{" "}
              the selected term -{" "}
              <strong>
                {!empty(selectedTermDetail) && !empty(selectedTermDetail.title)
                  ? selectedTermDetail.title
                  : ""}
              </strong>
              {selectedTermDetail && (
                <b>
                  {!empty(selectedTermDetail.name)
                    ? selectedTermDetail.name
                    : ""}
                </b>
              )}
              ?
            </span>
          }
        </div>
      </Dialog>
      <Toast ref={toastTR} style={{ zIndex: 99999 }} position="bottom-left" />
    </>
  );
};

export default Terms;
